import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  ButtonLink,
  Dialog,
  IconNewWindow,
  List,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';
import { type FC, useId } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { buildWhatAreSeekReferenceCheckArticleLink } from 'src/constants/urls';
import { TrackOnMount } from 'src/libs/Analytics';

import translations from './.vocab';

interface Props {
  onClose: () => void;
  open: boolean;
  trackReferenceCheckBalanceDialogDisplayedEvent: () => void;
  trackReferenceCheckBalanceDialogDismissedEvent: () => void;
}

export const ReferenceCheckExplainerDialog: FC<Props> = ({
  onClose,
  open,
  trackReferenceCheckBalanceDialogDisplayedEvent,
  trackReferenceCheckBalanceDialogDismissedEvent,
}) => {
  const { t } = useTranslations(translations);
  const id = useId();

  const { language, site } = useAppConfig();

  const handleClose = () => {
    trackReferenceCheckBalanceDialogDismissedEvent();
    onClose();
  };

  const readMoreHref = buildWhatAreSeekReferenceCheckArticleLink({
    language,
    site,
  });

  return (
    <Dialog
      id={id}
      closeLabel={t('Close')}
      onClose={handleClose}
      open={open}
      title={t('How to check references')}
    >
      <TrackOnMount onMount={trackReferenceCheckBalanceDialogDisplayedEvent}>
        <Stack
          space="large"
          data={{ testid: 'reference-check-balance-dialog-content' }}
        >
          <Box paddingY="xxsmall">
            {
              <List>
                <Text>
                  {t(
                    "Select <Strong>Check references</Strong> from the applicant profile, or the applicant card (after you've moved them to any folder past Prescreen).",
                    {
                      Strong: (children) => <Strong>{children}</Strong>,
                    },
                  )}
                </Text>
                <Text>
                  {t(
                    "We'll contact 2 referees for each applicant and ask them a set of standard questions.",
                  )}
                </Text>
                <Text>
                  {t(
                    "We'll notify you when the referees respond. Referees may reply at different times.",
                  )}
                </Text>
                <Text>
                  {t("You can download each referee's response as a report.")}
                </Text>
                <Text>
                  {t(
                    'You have 3 months from the date of purchase to use your reference checks. You can check references for up to 3 applicants.',
                  )}
                </Text>
              </List>
            }
          </Box>
          <Actions>
            <Button onClick={handleClose} variant="soft" tone="neutral">
              {t('Close')}
            </Button>
            {readMoreHref ? (
              <ButtonLink
                href={readMoreHref}
                target="_blank"
                tone="brandAccent"
                icon={<IconNewWindow />}
                iconPosition="trailing"
              >
                {t('More about reference checks')}
              </ButtonLink>
            ) : null}
          </Actions>
        </Stack>
      </TrackOnMount>
    </Dialog>
  );
};
